import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay } from 'date-fns';
import {
  Calendar, Check, MessageSquareText, Send, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, Clock, FileText, ImageIcon, ThumbsUp, ThumbsDown,
  Lightbulb, Eye, X, Hash, MapPin, Upload, CheckCircle2, PenLine, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Draft' },
  in_review: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'In Review' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Scheduled' },
  ready: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Ready' },
};

function PortalPostCard({ post, onFeedback, feedbackLoading, onSelect }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [comment, setComment] = useState('');
  const statusStyle = STATUS_STYLES[post.status] || STATUS_STYLES.draft;
  const approval = post.approval_status;

  const handleSubmitFeedback = async (status) => {
    await onFeedback(post.id, status, comment);
    setComment('');
    setShowFeedback(false);
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`portal-post-${post.id}`}
    >
      {/* Media preview — clickable */}
      {(post.image_url || (post.media_urls && post.media_urls.length > 0)) && (
        <div className="h-36 bg-gray-50 overflow-hidden cursor-pointer" onClick={onSelect}>
          <img
            src={resolveImageUrl(post.image_url || post.media_urls[0])}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Status + Date */}
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
            {statusStyle.label}
          </span>
          {post.scheduled_date && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {format(parseISO(post.scheduled_date), 'MMM d')}
              {post.scheduled_time && ` at ${post.scheduled_time}`}
            </span>
          )}
        </div>

        {/* Title + Content — clickable to open detail */}
        <div className="cursor-pointer" onClick={onSelect}>
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{post.title}</h3>
          {post.content && (
            <p className="text-xs text-gray-500 line-clamp-3 mt-1">{post.content}</p>
          )}
          <span className="text-[10px] text-blue-500 font-medium mt-1 inline-block hover:underline">
            View full post
          </span>
        </div>

        {/* Platform */}
        {post.platform && post.platform !== 'general' && (
          <span className="inline-block text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded">
            {post.platform}
          </span>
        )}

        {/* Approval Status */}
        {approval && (
          <div className={`text-xs font-medium px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 ${
            approval === 'approved'
              ? 'bg-green-50 text-green-700'
              : approval === 'approved_with_edits'
              ? 'bg-blue-50 text-blue-700'
              : approval === 'do_not_run'
              ? 'bg-red-50 text-red-700'
              : 'bg-orange-50 text-orange-700'
          }`}>
            {approval === 'approved' ? <ThumbsUp className="w-3 h-3" /> :
             approval === 'approved_with_edits' ? <PenLine className="w-3 h-3" /> :
             approval === 'do_not_run' ? <Ban className="w-3 h-3" /> :
             <ThumbsDown className="w-3 h-3" />}
            {approval === 'approved' ? 'Approved' :
             approval === 'approved_with_edits' ? 'Approved with edits' :
             approval === 'do_not_run' ? 'Do not run' :
             'Changes requested'}
          </div>
        )}

        {/* Feedback actions — tap card to open full detail for all options */}
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            onClick={() => handleSubmitFeedback('approved')}
            disabled={feedbackLoading || approval === 'approved'}
            className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`approve-post-${post.id}`}
          >
            {feedbackLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSelect}
            className="flex-1 h-8 text-xs border-gray-200 text-gray-600 hover:bg-gray-50"
            data-testid={`more-options-${post.id}`}
          >
            More Options
          </Button>
        </div>
      </div>
    </div>
  );
}

function MiniCalendar({ posts, currentMonth, onMonthChange, primaryColor }) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart);

  const postDates = new Set(
    posts
      .filter(p => p.scheduled_date)
      .map(p => {
        try { return format(parseISO(p.scheduled_date), 'yyyy-MM-dd'); } catch { return null; }
      })
      .filter(Boolean)
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="portal-mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => onMonthChange(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 bg-gray-50 text-gray-700 border border-gray-200 transition-colors"
          data-testid="cal-prev-month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-sm font-bold text-gray-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button
          onClick={() => onMonthChange(1)}
          className="p-2 rounded-lg hover:bg-gray-100 bg-gray-50 text-gray-700 border border-gray-200 transition-colors"
          data-testid="cal-next-month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-[10px] font-semibold text-gray-400 py-1">{d}</div>
        ))}
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const hasPost = postDates.has(dateStr);
          const isToday = isSameDay(day, new Date());
          return (
            <div
              key={dateStr}
              className={`relative text-xs py-1.5 rounded-lg font-medium ${
                isToday ? 'font-bold ring-2 ring-offset-1' : ''
              } ${hasPost ? 'text-white' : 'text-gray-600'}`}
              style={{
                backgroundColor: hasPost ? (primaryColor || '#1F467C') : undefined,
                ringColor: isToday ? (primaryColor || '#1F467C') : undefined,
              }}
            >
              {format(day, 'd')}
              {hasPost && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FacebookImageGrid({ images }) {
  if (!images || images.length === 0) return null;
  const count = images.length;

  if (count === 1) {
    return (
      <div className="bg-gray-100">
        <img src={images[0]} alt="" className="w-full max-h-[500px] object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
      </div>
    );
  }
  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-[2px] bg-gray-100">
        {images.map((url, i) => (
          <div key={i} className="h-[260px] overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
          </div>
        ))}
      </div>
    );
  }
  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-[2px] bg-gray-100">
        <div className="col-span-2 h-[280px] overflow-hidden">
          <img src={images[0]} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
        </div>
        {images.slice(1).map((url, i) => (
          <div key={i} className="h-[180px] overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
          </div>
        ))}
      </div>
    );
  }
  // 4+ images: 2x2 grid with "+N" overlay
  const shown = images.slice(0, 4);
  const extra = count - 4;
  return (
    <div className="grid grid-cols-2 gap-[2px] bg-gray-100">
      {shown.map((url, i) => (
        <div key={i} className="relative h-[200px] overflow-hidden">
          <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
          {i === 3 && extra > 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white text-2xl font-bold">+{extra}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PortalPostDetail({ post, onClose, onFeedback, feedbackLoading, primaryColor, client }) {
  const [comment, setComment] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState('edits'); // 'edits' or 'do_not_run'
  const statusStyle = STATUS_STYLES[post.status] || STATUS_STYLES.draft;
  const approval = post.approval_status;
  const allMedia = [
    ...(post.image_url ? [post.image_url] : []),
    ...(post.media_urls || []),
  ].filter((v, i, a) => a.indexOf(v) === i).map(resolveImageUrl).filter(Boolean);

  const handleSubmit = async (status) => {
    await onFeedback(post.id, status, comment);
    setComment('');
    setShowFeedbackForm(false);
  };

  const clientName = client?.name || 'Business Page';
  const clientInitial = clientName.charAt(0).toUpperCase();

  // Format date like Facebook does
  const fbDate = (() => {
    if (!post.scheduled_date) return '';
    try {
      const d = parseISO(post.scheduled_date);
      const time = post.scheduled_time || format(d, 'h:mm a');
      return `${format(d, 'MMMM d, yyyy')} at ${time}`;
    } catch {
      return '';
    }
  })();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="portal-post-detail-overlay"
    >
      <div
        className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-lg sm:my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="portal-post-detail"
      >
        {/* Modal close + status bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
            {post.platform && post.platform !== 'general' && (
              <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded capitalize">
                {post.platform}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
            data-testid="close-post-detail"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* === Facebook Post Card === */}
        <div className="border-b border-gray-100">
          {/* FB Header: Avatar + Page Name + Time */}
          <div className="flex items-center gap-2.5 px-4 pt-3 pb-2">
            {client?.logo_url ? (
              <img
                src={client.logo_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
              />
            ) : null}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{
                backgroundColor: primaryColor || '#1877F2',
                display: client?.logo_url ? 'none' : 'flex',
              }}
            >
              {clientInitial}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[#050505] leading-tight truncate">
                {clientName}
              </p>
              <div className="flex items-center gap-1 text-[12px] text-[#65676B]">
                {fbDate && <span>{fbDate}</span>}
                <span>·</span>
                <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14.5a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13zM7 3.5a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm4.5 4.5a4.5 4.5 0 0 1-9 0h1.3a3.2 3.2 0 0 0 6.4 0H12.5z"/></svg>
              </div>
            </div>
          </div>

          {/* FB Caption (above images, like real Facebook) */}
          <div className="px-4 pb-2">
            {post.title && (
              <p className="text-[15px] font-semibold text-[#050505] mb-1" data-testid="post-detail-title">
                {post.title}
              </p>
            )}
            {post.content && (
              <p className="text-[15px] text-[#050505] leading-[1.33] whitespace-pre-wrap" data-testid="post-detail-content">
                {post.content}
              </p>
            )}
            {post.hashtags && post.hashtags.length > 0 && (
              <p className="text-[15px] text-[#0064D1] mt-1">
                {post.hashtags.join(' ')}
              </p>
            )}
          </div>

          {/* FB Image Grid */}
          <FacebookImageGrid images={allMedia} />

          {/* FB Reaction Bar (cosmetic) */}
          <div className="px-4">
            <div className="flex items-center justify-between py-2.5 border-b border-gray-200">
              <div className="flex items-center gap-1">
                <div className="flex -space-x-1">
                  <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#1877F2] text-white">
                    <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="currentColor"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.457 5.009L4.88 8.44H6.41v4.552h2.168V8.44h1.63l.28-1.63H8.578V5.559c0-.472.132-.664.765-.664H10.5V3.007c-.345-.048-.796-.082-1.224-.082-1.577 0-2.82.766-2.82 2.084z"/></svg>
                  </span>
                  <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#F0382B]">
                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 16 16" fill="currentColor"><path d="M8 15.016c-.26 0-.508-.1-.694-.276l-5.08-4.704C.708 8.612 0 6.818 0 5.387c0-2.967 2.086-4.87 4.62-4.87 1.386 0 2.534.66 3.38 1.666.846-1.006 1.994-1.666 3.38-1.666C13.914.517 16 2.42 16 5.387c0 1.43-.708 3.225-2.226 4.65l-5.08 4.703a.978.978 0 0 1-.694.276z"/></svg>
                  </span>
                </div>
                <span className="text-[13px] text-[#65676B] ml-1">23</span>
              </div>
              <div className="flex items-center gap-3 text-[13px] text-[#65676B]">
                <span>4 comments</span>
                <span>2 shares</span>
              </div>
            </div>

            {/* Like / Comment / Share buttons */}
            <div className="grid grid-cols-3 py-1">
              {[
                { label: 'Like', icon: (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                )},
                { label: 'Comment', icon: (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                )},
                { label: 'Share', icon: (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                )},
              ].map(btn => (
                <div key={btn.label} className="flex items-center justify-center gap-1.5 py-2 rounded-md text-[#65676B] cursor-default">
                  {btn.icon}
                  <span className="text-[13px] font-semibold">{btn.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* === Below the FB card: Meta info, Feedback, Actions === */}
        <div className="px-4 py-4 space-y-3">
          {/* Location */}
          {post.location && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin className="w-3.5 h-3.5" />
              {post.location}
            </div>
          )}

          {/* Visual notes from agency */}
          {post.visual_notes && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-[10px] font-bold uppercase text-blue-500 mb-1">Creative Direction</p>
              <p className="text-sm text-blue-800">{post.visual_notes}</p>
            </div>
          )}

          {/* Approval Status */}
          {approval && (
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
              approval === 'approved'
                ? 'bg-emerald-50 text-emerald-700'
                : approval === 'approved_with_edits'
                ? 'bg-blue-50 text-blue-700'
                : approval === 'do_not_run'
                ? 'bg-red-50 text-red-700'
                : 'bg-orange-50 text-orange-700'
            }`}>
              {approval === 'approved' ? <ThumbsUp className="w-4 h-4" /> :
               approval === 'approved_with_edits' ? <PenLine className="w-4 h-4" /> :
               approval === 'do_not_run' ? <Ban className="w-4 h-4" /> :
               <ThumbsDown className="w-4 h-4" />}
              {approval === 'approved' ? 'You approved this post' :
               approval === 'approved_with_edits' ? 'Approved with your edits' :
               approval === 'do_not_run' ? 'Marked as do not run' :
               'You requested changes on this post'}
            </div>
          )}

          {/* Previous feedback history */}
          {post.client_feedback && post.client_feedback.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase text-gray-400 tracking-wider">Feedback History</p>
              {post.client_feedback.map((fb, i) => (
                <div
                  key={i}
                  className={`p-2.5 rounded-lg text-xs border-l-[3px] ${
                    fb.status === 'approved'
                      ? 'bg-emerald-50/50 border-emerald-400'
                      : fb.status === 'approved_with_edits'
                      ? 'bg-blue-50/50 border-blue-400'
                      : fb.status === 'do_not_run'
                      ? 'bg-red-50/50 border-red-400'
                      : 'bg-orange-50/50 border-orange-400'
                  }`}
                >
                  <span className={`text-[10px] font-bold uppercase ${
                    fb.status === 'approved' ? 'text-emerald-600' :
                    fb.status === 'approved_with_edits' ? 'text-blue-600' :
                    fb.status === 'do_not_run' ? 'text-red-600' :
                    'text-orange-600'
                  }`}>
                    {fb.status === 'approved' ? 'Approved' :
                     fb.status === 'approved_with_edits' ? 'Approved with Edits' :
                     fb.status === 'do_not_run' ? 'Do Not Run' :
                     'Changes Requested'}
                  </span>
                  {fb.comment && <p className="text-gray-600 mt-0.5">{fb.comment}</p>}
                  {fb.submitted_at && (
                    <p className="text-gray-400 text-[10px] mt-1">{format(parseISO(fb.submitted_at), 'MMM d, yyyy h:mm a')}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-3 sm:rounded-b-xl">
          {!showFeedbackForm ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSubmit('approved')}
                  disabled={feedbackLoading || approval === 'approved'}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm"
                  data-testid="detail-approve-btn"
                >
                  {feedbackLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setFeedbackMode('edits'); setShowFeedbackForm(true); }}
                  disabled={feedbackLoading}
                  className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
                  data-testid="detail-edits-btn"
                >
                  <PenLine className="w-4 h-4 mr-1.5" />
                  Approved with Edits
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={() => { setFeedbackMode('do_not_run'); setShowFeedbackForm(true); }}
                disabled={feedbackLoading}
                className="w-full border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                data-testid="detail-do-not-run-btn"
              >
                <Ban className="w-4 h-4 mr-1.5" />
                Do Not Run - See Note
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500">
                {feedbackMode === 'edits' ? 'What edits would you like made?' : 'Why should this post not run?'}
              </p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={feedbackMode === 'edits'
                  ? 'Describe your edits (e.g. change the wording, swap the photo...)'
                  : 'Add a note explaining why this post should not be published...'}
                rows={3}
                className={`w-full text-sm px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:outline-none ${
                  feedbackMode === 'edits'
                    ? 'border-blue-200 focus:ring-blue-300'
                    : 'border-red-200 focus:ring-red-300'
                }`}
                autoFocus
                data-testid="detail-feedback-textarea"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSubmit(feedbackMode === 'edits' ? 'approved_with_edits' : 'do_not_run')}
                  disabled={feedbackLoading || !comment.trim()}
                  className={`flex-1 text-white ${
                    feedbackMode === 'edits'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                  data-testid="detail-submit-feedback-btn"
                >
                  {feedbackLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {feedbackMode === 'edits' ? 'Submit Edits' : 'Confirm Do Not Run'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setShowFeedbackForm(false); setComment(''); setFeedbackMode('edits'); }}
                  className="text-gray-400"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientPortalPage() {
  const { token, clientSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');

  // Idea form
  const [ideaText, setIdeaText] = useState('');
  const [ideaSubmitting, setIdeaSubmitting] = useState(false);
  const [ideaSuccess, setIdeaSuccess] = useState(false);

  // Post detail
  const [selectedPost, setSelectedPost] = useState(null);

  // Log post view to activity tracker
  const handleSelectPost = (post) => {
    setSelectedPost(post);
    try {
      fetch(`${API}/portal/${token}/posts/${post.id}/view`, { method: 'POST' }).catch(() => {});
    } catch {}
  };

  // Calendar month
  const [calMonth, setCalMonth] = useState(new Date());

  // Photo upload
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadUploading, setUploadUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchPortalData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/portal/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Portal not found');
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  const handleFeedback = async (postId, status, comment) => {
    setFeedbackLoading(true);
    try {
      const res = await fetch(`${API}/portal/${token}/posts/${postId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, comment }),
      });
      if (!res.ok) throw new Error('Failed to submit feedback');
      await fetchPortalData();
    } catch (err) {
      console.error(err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleIdeaSubmit = async (e) => {
    e.preventDefault();
    if (!ideaText.trim()) return;
    setIdeaSubmitting(true);
    try {
      const res = await fetch(`${API}/portal/${token}/ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ideaText.trim() }),
      });
      if (!res.ok) throw new Error('Failed to submit idea');
      setIdeaText('');
      setIdeaSuccess(true);
      setTimeout(() => setIdeaSuccess(false), 3000);
      await fetchPortalData();
    } catch (err) {
      console.error(err);
    } finally {
      setIdeaSubmitting(false);
    }
  };

  const handleMonthChange = (dir) => {
    setCalMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + dir);
      return d;
    });
  };

  const handlePortalUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploadUploading(true);
    setUploadResults(null);
    let success = 0;
    let failed = 0;

    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/portal/${token}/upload?category=Client+Upload`, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        success++;
      } catch {
        failed++;
      }
    }

    setUploadResults({ success, failed, total: uploadFiles.length });
    setUploadUploading(false);
    setUploadFiles([]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    setUploadFiles(prev => [...prev, ...droppedFiles]);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-500">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="portal-error">
        <div className="text-center space-y-3 max-w-sm px-6">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
          <h1 className="text-lg font-bold text-gray-900">Portal Not Found</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const { client, posts, ideas } = data;
  const primaryColor = client.primary_color || '#1F467C';

  const filteredPosts = posts.filter(p => {
    if (!p.scheduled_date) return true;
    try {
      const postDate = parseISO(p.scheduled_date);
      return (
        postDate.getMonth() === calMonth.getMonth() &&
        postDate.getFullYear() === calMonth.getFullYear()
      );
    } catch {
      return true;
    }
  });

  // Count posts in the next month for the "continue" prompt
  const nextMonthDate = new Date(calMonth);
  nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
  const nextMonthPosts = posts.filter(p => {
    if (!p.scheduled_date) return false;
    try {
      const d = parseISO(p.scheduled_date);
      return d.getMonth() === nextMonthDate.getMonth() && d.getFullYear() === nextMonthDate.getFullYear();
    } catch { return false; }
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-portal-page">
      {/* Header */}
      <header
        className="sticky top-0 z-50 border-b backdrop-blur-md bg-white/90"
        style={{ borderBottomColor: primaryColor + '30' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {client.logo_url && (
              <img
                src={client.logo_url}
                alt=""
                className="h-9 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <h1 className="text-base font-bold text-gray-900" data-testid="portal-client-name">
                {client.name}
              </h1>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">
                Content Review Portal
              </p>
            </div>
          </div>
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: primaryColor }}
            title="Connected"
          />
        </div>
      </header>

      {/* Welcome message */}
      {client.portal_welcome && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <p
            className="text-sm text-gray-600 leading-relaxed border-l-[3px] pl-3"
            style={{ borderColor: primaryColor }}
            data-testid="portal-welcome-message"
          >
            {client.portal_welcome}
          </p>
        </div>
      )}

      {/* Tab bar */}
      <div className="sticky top-[57px] z-40 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1">
          {[
            { key: 'posts', label: 'Upcoming Posts', icon: Calendar, count: posts.length },
            { key: 'upload', label: 'Upload Photos', icon: Upload },
            { key: 'ideas', label: 'Submit Ideas', icon: Lightbulb },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab.key
                  ? 'border-current'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
              style={activeTab === tab.key ? { color: primaryColor } : {}}
              data-testid={`portal-tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'posts' && (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            {/* Sidebar: Mini Calendar */}
            <div className="space-y-4">
              <MiniCalendar
                posts={posts}
                currentMonth={calMonth}
                onMonthChange={handleMonthChange}
                primaryColor={primaryColor}
              />
              {/* Stats */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">This Month</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold" style={{ color: primaryColor }}>{filteredPosts.length}</div>
                    <div className="text-[10px] text-gray-400">Posts</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-emerald-600">
                      {filteredPosts.filter(p => p.approval_status === 'approved').length}
                    </div>
                    <div className="text-[10px] text-gray-400">Approved</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Post grid */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-gray-900">
                  {format(calMonth, 'MMMM yyyy')} Posts
                </h2>
                <span className="text-xs text-gray-400">
                  {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Approval Progress Bar */}
              {filteredPosts.length > 0 && (() => {
                const reviewed = filteredPosts.filter(p =>
                  p.approval_status && p.approval_status !== 'pending'
                ).length;
                const total = filteredPosts.length;
                const pct = Math.round((reviewed / total) * 100);
                return (
                  <div className="mb-5 bg-white rounded-xl border border-gray-200 p-4" data-testid="portal-approval-progress">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Review Progress</span>
                      <span className="text-xs font-semibold text-gray-500">
                        {reviewed} of {total} reviewed
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: primaryColor }}
                        data-testid="portal-progress-fill"
                      />
                    </div>
                    {reviewed === total && total > 0 && (
                      <p className="text-xs text-emerald-600 font-medium mt-2 text-center">
                        All posts reviewed — thank you!
                      </p>
                    )}
                  </div>
                );
              })()}
              {filteredPosts.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No posts scheduled for this month yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Try navigating to another month.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="portal-posts-grid">
                  {filteredPosts.map(post => (
                    <PortalPostCard
                      key={post.id}
                      post={post}
                      onFeedback={handleFeedback}
                      feedbackLoading={feedbackLoading}
                      onSelect={() => handleSelectPost(post)}
                    />
                  ))}
                </div>
              )}

              {/* Continue to next month prompt */}
              {filteredPosts.length > 0 && (
                <div
                  className="mt-8 rounded-xl border-2 border-dashed border-gray-200 bg-white p-6 text-center"
                  data-testid="portal-next-month-cta"
                >
                  <p className="text-sm text-gray-500 mb-1">
                    Done reviewing {format(calMonth, 'MMMM')}?
                  </p>
                  {nextMonthPosts.length > 0 ? (
                    <>
                      <p className="text-lg font-bold text-gray-900 mb-3">
                        {nextMonthPosts.length} post{nextMonthPosts.length !== 1 ? 's' : ''} ready for review in {format(nextMonthDate, 'MMMM')}
                      </p>
                      <Button
                        onClick={() => handleMonthChange(1)}
                        className="rounded-full px-6 text-white"
                        style={{ backgroundColor: primaryColor }}
                        data-testid="portal-continue-next-month-btn"
                      >
                        Continue to {format(nextMonthDate, 'MMMM')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">
                      No posts scheduled for {format(nextMonthDate, 'MMMM')} yet.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="max-w-xl mx-auto space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="portal-upload-section">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-1">
                <Upload className="w-5 h-5" style={{ color: primaryColor }} />
                Upload Photos
              </h2>
              <p className="text-xs text-gray-400 mb-4">
                Share your photos with the team — product shots, event photos, team pictures, anything you'd like us to use in your posts.
              </p>

              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="portal-drop-zone"
              >
                <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag & drop photos here, or
                </p>
                <label className="cursor-pointer">
                  <span
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Upload className="w-4 h-4" />
                    Choose Files
                  </span>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/quicktime,.heic,.heif"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setUploadFiles(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                    className="hidden"
                    data-testid="portal-file-input"
                  />
                </label>
                <p className="text-[10px] text-gray-400 mt-3">JPG, PNG, GIF, WebP, HEIC, MP4, MOV — up to 200MB each</p>
              </div>

              {/* Selected files list */}
              {uploadFiles.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="portal-upload-files-list">
                  <p className="text-xs font-semibold text-gray-600">{uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected:</p>
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <span className="truncate flex-1 text-gray-700">{f.name}</span>
                      <span className="text-xs text-gray-400 mx-2">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                      <button
                        onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-red-500 p-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <Button
                    onClick={handlePortalUpload}
                    disabled={uploadUploading}
                    className="w-full text-white mt-3"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="portal-upload-btn"
                  >
                    {uploadUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload {uploadFiles.length} Photo{uploadFiles.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Upload results */}
              {uploadResults && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2" data-testid="portal-upload-success">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {uploadResults.success} of {uploadResults.total} photo{uploadResults.total !== 1 ? 's' : ''} uploaded!
                    {uploadResults.failed > 0 && ` (${uploadResults.failed} failed)`}
                    {' '}The team will see them in the library.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ideas' && (
          <div className="max-w-xl mx-auto space-y-6">
            {/* Submit idea form */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="portal-idea-form">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-1">
                <Lightbulb className="w-5 h-5" style={{ color: primaryColor }} />
                Share a Post Idea
              </h2>
              <p className="text-xs text-gray-400 mb-4">
                Have an idea for a social post? Describe it below and the team will review it.
              </p>

              <form onSubmit={handleIdeaSubmit} className="space-y-3">
                <textarea
                  value={ideaText}
                  onChange={(e) => setIdeaText(e.target.value)}
                  placeholder="Describe your post idea... What should we share? Any specific dates or events?"
                  rows={4}
                  className="w-full text-sm px-4 py-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': primaryColor + '50' }}
                  data-testid="idea-text-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">
                    {ideaText.length}/500
                  </span>
                  <Button
                    type="submit"
                    disabled={!ideaText.trim() || ideaSubmitting}
                    className="text-white"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="submit-idea-btn"
                  >
                    {ideaSubmitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Submit Idea
                  </Button>
                </div>
              </form>

              {ideaSuccess && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2" data-testid="idea-success-msg">
                  <Check className="w-4 h-4" />
                  Idea submitted! The team will review it shortly.
                </div>
              )}
            </div>

            {/* Previous ideas */}
            {ideas && ideas.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-900">Your Submitted Ideas</h3>
                {ideas.map(idea => (
                  <div
                    key={idea.id}
                    className="bg-white rounded-xl border border-gray-200 p-4"
                    data-testid={`portal-idea-${idea.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm text-gray-800">{idea.description || idea.title}</p>
                        <span className="text-[10px] text-gray-400 mt-1 block">
                          {idea.created_at && format(parseISO(idea.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                        idea.status === 'submitted'
                          ? 'bg-blue-100 text-blue-700'
                          : idea.status === 'in_progress'
                          ? 'bg-amber-100 text-amber-700'
                          : idea.status === 'completed'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {idea.status}
                      </span>
                    </div>
                    {idea.agency_response && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                        <strong>Team response:</strong> {idea.agency_response}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Post Detail Modal */}
      {selectedPost && (
        <PortalPostDetail
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onFeedback={async (postId, status, comment) => {
            await handleFeedback(postId, status, comment);
            setSelectedPost(null);
          }}
          feedbackLoading={feedbackLoading}
          primaryColor={primaryColor}
          client={client}
        />
      )}

      {/* Footer */}
      <footer className="border-t border-gray-100 py-6 mt-8">
        <p className="text-center text-[10px] text-gray-400">
          Powered by Smarter Posting
        </p>
      </footer>
    </div>
  );
}
